import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Table,
  Input,
  Breadcrumb,
  Tag,
  DatePicker,
  Row,
  Col,
  Grid,
  message,
  Select,
  Card,
  Typography,
  Space,
  Dropdown,
  Tooltip,
  Empty,
  Avatar,
} from 'antd';
import { Link } from 'react-router-dom';
import {
  RightOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  MoreOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  UserOutlined,
  AuditOutlined,
} from '@ant-design/icons';
import { walletService, citiesService } from '../../common/services';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

dayjs.locale('fr');
dayjs.extend(relativeTime);

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const useDebounce = (value, delay = 400) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
};

const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  let digits = String(phone).replace(/\D/g, '');
  if (digits.startsWith('225')) digits = digits.slice(3);
  if (digits.length === 9) digits = '0' + digits;
  return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

const formatNumber = (num) => new Intl.NumberFormat('fr-FR').format(num || 0);

const adminFullName = (admin) => {
  if (!admin) return '';
  const v = `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
  return v || (admin.phone ? formatPhoneNumber(admin.phone) : '');
};

const AjustementsAdmin = () => {
  const [data, setData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    credits: 0,
    debits: 0,
    net: 0,
  });

  const [phDateRange, setPhDateRange] = useState(null);
  const [phCity, setPhCity] = useState(null);
  const [phQuery, setPhQuery] = useState('');
  const phQueryDebounced = useDebounce(phQuery, 400);

  const [cities, setCities] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [sorter, setSorter] = useState({ field: 'date', order: 'descend' });

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;

  const apiParams = useMemo(() => {
    let sortParam = '-date';
    if (sorter?.field && sorter?.order) {
      sortParam = sorter.order === 'descend' ? `-${sorter.field}` : `${sorter.field}`;
    }
    const params = {
      page,
      limit,
      sort: sortParam,
      type: 'ajustement_admin',
    };
    if (phCity) params.cityCode = phCity;
    if (phQueryDebounced) params.q = phQueryDebounced.replace(/\s+/g, '');
    if (phDateRange && phDateRange.length === 2 && phDateRange[0] && phDateRange[1]) {
      params.start = phDateRange[0].format('YYYY-MM-DD');
      params.end = phDateRange[1].format('YYYY-MM-DD');
    }
    return params;
  }, [page, limit, sorter, phCity, phQueryDebounced, phDateRange]);

  const fetchAllPages = async (baseParams) => {
    const rows = [];
    let pageIndex = 1;
    const pageSize = 1000;
    let totalCount = Infinity;

    while (rows.length < totalCount) {
      const res = await walletService.historiquePoints({
        ...baseParams,
        page: pageIndex,
        limit: pageSize,
      });
      const items = Array.isArray(res?.items) ? res.items : [];
      rows.push(...items);
      totalCount = res?.total ?? rows.length;
      if (!items.length) break;
      pageIndex += 1;
      if (res?.totalPages && pageIndex > res.totalPages) break;
    }
    return rows;
  };

  const mapForExport = (rows) =>
    rows.map((it) => ({
      Admin: adminFullName(it.admin) || '—',
      'Téléphone admin': it.admin?.phone ? formatPhoneNumber(it.admin.phone) : '',
      Chauffeur: `${it.lastName || ''} ${it.firstName || ''}`.trim(),
      'Téléphone chauffeur': formatPhoneNumber(it.phone),
      Ville: it.ville?.nom || '',
      Description: it.description,
      Points: it.points,
      Date: dayjs(it.date).format('DD/MM/YYYY HH:mm:ss'),
    }));

  const load = async () => {
    try {
      setLoading(true);
      const res = await walletService.historiquePoints(apiParams);
      const items = Array.isArray(res?.items) ? res.items : [];
      setData(items);
      setTotal(res?.total || 0);

      const apiCredits = Number(res?.sumPositive ?? 0);
      const apiDebits = Math.abs(Number(res?.sumNegative ?? 0));
      const apiNet = Number(res?.sumPoints ?? (apiCredits - apiDebits));

      setStats({
        total: res?.total || items.length,
        credits: apiCredits,
        debits: apiDebits,
        net: apiNet,
      });

      if (res?.page && res.page !== page) setPage(res.page);
      if (res?.limit && res.limit !== limit) setLimit(res.limit);
    } catch (err) {
      console.error('Erreur API:', err);
      message.error("Impossible de charger les ajustements admin.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [apiParams]);

  useEffect(() => {
    (async () => {
      try {
        const res = await citiesService.getAll();
        const list = Array.isArray(res) ? res : (res?.data || res?.items || res?.cities || []);
        setCities(list.filter(c => c?.isActive !== false));
      } catch (e) {
        console.error('Erreur chargement villes:', e);
      }
    })();
  }, []);

  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Préparation de l\'export Excel…', key: 'exp' });
      const all = await fetchAllPages(apiParams);
      const ws = XLSX.utils.json_to_sheet(mapForExport(all));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Ajustements Admin');
      XLSX.writeFile(wb, `ajustements_admin_${dayjs().format('YYYY-MM-DD')}.xlsx`);
      message.success({ content: 'Export Excel terminé !', key: 'exp' });
    } catch (e) {
      console.error(e);
      message.error({ content: 'Échec de l\'export Excel.', key: 'exp' });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToCSV = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Préparation de l\'export CSV…', key: 'exp' });
      const all = await fetchAllPages(apiParams);
      const ws = XLSX.utils.json_to_sheet(mapForExport(all));
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `ajustements_admin_${dayjs().format('YYYY-MM-DD')}.csv`);
      message.success({ content: 'Export CSV terminé !', key: 'exp' });
    } catch (e) {
      console.error(e);
      message.error({ content: 'Échec de l\'export CSV.', key: 'exp' });
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Préparation du PDF…', key: 'exp' });
      const all = await fetchAllPages(apiParams);
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text('Ajustements Admin', 14, 22);
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Généré le ${dayjs().format('DD/MM/YYYY à HH:mm')}`, 14, 30);

      autoTable(doc, {
        startY: 38,
        head: [['Admin', 'Chauffeur', 'Téléphone', 'Ville', 'Description', 'Points', 'Date']],
        body: all.map((it) => [
          adminFullName(it.admin) || '—',
          `${it.lastName || ''} ${it.firstName || ''}`.trim(),
          formatPhoneNumber(it.phone),
          it.ville?.nom || '',
          (it.description || '').substring(0, 50) + ((it.description || '').length > 50 ? '...' : ''),
          String(it.points),
          dayjs(it.date).format('DD/MM/YYYY HH:mm'),
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [114, 46, 209] },
      });
      doc.save(`ajustements_admin_${dayjs().format('YYYY-MM-DD')}.pdf`);
      message.success({ content: 'Export PDF terminé !', key: 'exp' });
    } catch (e) {
      console.error(e);
      message.error({ content: 'Échec de l\'export PDF.', key: 'exp' });
    } finally {
      setIsExporting(false);
    }
  };

  const exportMenuItems = [
    { key: 'excel', label: 'Excel (.xlsx)', icon: <FileExcelOutlined style={{ color: '#217346' }} />, onClick: exportToExcel },
    { key: 'csv',   label: 'CSV',           icon: <DownloadOutlined style={{ color: '#1890ff' }} />, onClick: exportToCSV   },
    { key: 'pdf',   label: 'PDF',           icon: <FilePdfOutlined style={{ color: '#ff4d4f' }} />, onClick: exportToPDF   },
  ];

  const columns = [
    {
      title: 'Admin',
      key: 'admin',
      width: 220,
      render: (_, record) => {
        const name = adminFullName(record.admin);
        if (!name && !record.admin?.id) {
          return (
            <Tooltip title="Auteur inconnu (ajustement antérieur au suivi)">
              <Tag icon={<UserOutlined />} color="default" style={{ fontStyle: 'italic' }}>
                Inconnu
              </Tag>
            </Tooltip>
          );
        }
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.3 }}>
            <Avatar size="small" style={{ backgroundColor: '#722ed1' }}>
              {(name || 'A').charAt(0).toUpperCase()}
            </Avatar>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block' }}>
                {name || <Text type="secondary">—</Text>}
              </Text>
              {record.admin?.role ? (
                <Text type="secondary" style={{ fontSize: 11, textTransform: 'capitalize' }}>
                  {record.admin.role}
                </Text>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Chauffeur',
      key: 'chauffeur',
      width: 200,
      render: (_, record) => {
        const fullName = `${record.firstName || ''} ${record.lastName || ''}`.trim();
        return (
          <div style={{ lineHeight: 1.3 }}>
            <Text strong style={{ fontSize: 13, display: 'block' }}>
              {fullName || <Text type="secondary">—</Text>}
            </Text>
            {record.phone ? (
              <Text
                type="secondary"
                copyable={{
                  text: (record.phone || '').replace('+225', '').replace(/\s+/g, ''),
                  tooltips: ['Copier le numéro', 'Numéro copié !'],
                }}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              >
                <PhoneOutlined style={{ marginRight: 4, color: '#8c8c8c' }} />
                {formatPhoneNumber(record.phone)}
              </Text>
            ) : null}
          </div>
        );
      },
      responsive: ['sm'],
    },
    {
      title: 'Ville',
      key: 'ville',
      width: 130,
      filters: cities.map(c => ({ text: c.name, value: c.code })),
      filterMultiple: false,
      filteredValue: phCity ? [phCity] : null,
      render: (_, record) => {
        const nom = record.ville?.nom;
        if (!nom) return <Text type="secondary">-</Text>;
        return (
          <Tag icon={<EnvironmentOutlined />} color="geekblue" style={{ fontSize: 12 }}>
            {nom}
          </Tag>
        );
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 320,
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <Text ellipsis style={{ fontSize: 13 }}>{text}</Text>
        </Tooltip>
      ),
    },
    {
      title: 'Points',
      dataIndex: 'points',
      key: 'points',
      width: 110,
      align: 'right',
      render: (points) => {
        const isPositive = points >= 0;
        return (
          <Tag
            color={isPositive ? 'success' : 'error'}
            style={{
              fontWeight: 600,
              fontSize: isMobile ? 12 : 13,
              padding: '4px 12px',
              borderRadius: 20,
              minWidth: 70,
              textAlign: 'center',
            }}
          >
            {isPositive ? '+' : ''}{formatNumber(points)}
          </Tag>
        );
      },
      sorter: true,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: isMobile ? 110 : 170,
      render: (date) => (
        <Tooltip title={dayjs(date).format('dddd DD MMMM YYYY à HH:mm:ss')}>
          <Space size={4} direction={isMobile ? 'vertical' : 'horizontal'}>
            <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
            <span style={{ fontSize: isMobile ? 11 : 13 }}>
              {isMobile ? dayjs(date).format('DD/MM HH:mm') : dayjs(date).format('DD/MM/YYYY HH:mm')}
            </span>
          </Space>
        </Tooltip>
      ),
      sorter: true,
      defaultSortOrder: 'descend',
    },
  ];

  return (
    <div>
      <Breadcrumb
        separator={<RightOutlined />}
        style={{ marginBottom: 20 }}
        items={[
          { title: <Link to="/dashboard">Accueil</Link> },
          { title: <span>Wallet</span> },
          { title: <span style={{ fontWeight: 600 }}>Ajustements Admin</span> },
        ]}
      />

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={isMobile ? 4 : 3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
            <AuditOutlined style={{ color: '#722ed1' }} />
            Ajustements Admin
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Modifications manuelles de points effectuées par les administrateurs
          </Text>
        </div>
        <Space wrap>
          <Button
            icon={<ReloadOutlined spin={isLoading} />}
            onClick={load}
            disabled={isLoading}
          >
            {!isMobile && 'Actualiser'}
          </Button>
          <Dropdown
            menu={{ items: exportMenuItems }}
            placement="bottomRight"
            disabled={isExporting}
          >
            <Button type="primary" icon={<DownloadOutlined />} loading={isExporting}>
              Exporter {!isMobile && <MoreOutlined />}
            </Button>
          </Dropdown>
        </Space>
      </div>

      <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card
            style={{ background: 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)', border: 'none', borderRadius: 12 }}
            bodyStyle={{ padding: isMobile ? 12 : 16, textAlign: 'center' }}
          >
            <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: '#2f54eb', display: 'block' }}>
              {formatNumber(stats.total)}
            </Text>
            <Text style={{ fontSize: 11, color: '#595959' }}>Total ajustements</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            style={{ background: 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)', border: 'none', borderRadius: 12 }}
            bodyStyle={{ padding: isMobile ? 12 : 16, textAlign: 'center' }}
          >
            <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: '#52c41a', display: 'block' }}>
              +{formatNumber(stats.credits)}
            </Text>
            <Text style={{ fontSize: 11, color: '#595959' }}>Total crédits</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            style={{ background: 'linear-gradient(135deg, #fff1f0 0%, #ffccc7 100%)', border: 'none', borderRadius: 12 }}
            bodyStyle={{ padding: isMobile ? 12 : 16, textAlign: 'center' }}
          >
            <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: '#ff4d4f', display: 'block' }}>
              -{formatNumber(stats.debits)}
            </Text>
            <Text style={{ fontSize: 11, color: '#595959' }}>Total débits</Text>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card
            style={{ background: 'linear-gradient(135deg, #fffbe6 0%, #fff1b8 100%)', border: 'none', borderRadius: 12 }}
            bodyStyle={{ padding: isMobile ? 12 : 16, textAlign: 'center' }}
          >
            <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: 700, color: '#faad14', display: 'block' }}>
              {stats.net >= 0 ? '+' : ''}{formatNumber(stats.net)}
            </Text>
            <Text style={{ fontSize: 11, color: '#595959' }}>Net</Text>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 24, borderRadius: 12 }} bodyStyle={{ padding: isMobile ? 16 : 20 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={12} lg={8}>
            <RangePicker
              size={isMobile ? 'middle' : 'large'}
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={phDateRange}
              onChange={(dates) => { setPage(1); setPhDateRange(dates); }}
              placeholder={['Date début', 'Date fin']}
              allowClear
              suffixIcon={<CalendarOutlined />}
            />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <Select
              size={isMobile ? 'middle' : 'large'}
              allowClear
              showSearch
              style={{ width: '100%' }}
              placeholder={
                <span>
                  <EnvironmentOutlined style={{ marginRight: 8 }} />
                  Filtrer par ville
                </span>
              }
              value={phCity}
              onChange={(val) => { setPage(1); setPhCity(val); }}
              optionFilterProp="label"
              options={cities.map(c => ({ value: c.code, label: c.name }))}
            />
          </Col>
          <Col xs={24} md={24} lg={10}>
            <Input
              size={isMobile ? 'middle' : 'large'}
              allowClear
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Rechercher par téléphone, description..."
              value={phQuery}
              onChange={(e) => { setPage(1); setPhQuery(e.target.value); }}
              style={{ borderRadius: 8 }}
            />
          </Col>
        </Row>
      </Card>

      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        <Table
          loading={isLoading}
          size={isMobile ? 'small' : 'middle'}
          columns={columns}
          dataSource={data}
          rowKey={(record) => record._id || `${record.phone}-${record.date}-${Math.random()}`}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: !isMobile,
            showQuickJumper: !isMobile,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {`${range[0]}-${range[1]} sur ${formatNumber(total)} ajustements`}
              </Text>
            ),
            style: { padding: '12px 16px', margin: 0 },
          }}
          onChange={(pagination, filters, sorterArg) => {
            const newCity = filters?.ville?.length ? filters.ville[0] : null;
            const cityChanged = newCity !== phCity;
            if (cityChanged) {
              setPhCity(newCity);
              setPage(1);
            } else {
              setPage(pagination.current);
            }
            setLimit(pagination.pageSize);
            const s = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
            if (s && s.field) {
              setSorter({ field: s.field, order: s.order });
            } else {
              setSorter({ field: 'date', order: 'descend' });
            }
          }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">Aucun ajustement trouvé</Text>}
              />
            ),
          }}
          rowClassName={(_, index) => index % 2 === 0 ? 'table-row-light' : 'table-row-dark'}
        />
      </Card>

      <style>{`
        .table-row-light { background-color: #ffffff; }
        .table-row-dark { background-color: #fafafa; }
        .table-row-light:hover td,
        .table-row-dark:hover td { background-color: #f9f0ff !important; }
        .ant-table-thead > tr > th {
          background-color: #fafafa !important;
          font-weight: 600 !important;
          color: #262626 !important;
        }
        .ant-pagination {
          background-color: #fafafa;
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
    </div>
  );
};

export default AjustementsAdmin;
